import { Button, H5, H6, InputCheckbox } from "@sellar-io/components";
import { ReactElement, useEffect, useMemo, useRef, useState } from "react";
import { useSellarUser } from "../../../../hooks";
import { useSellarCurrentBusiness } from "../../../../hooks/useSellarCurrentBusiness.shared";
import { Navigate, useNavigate } from "react-router";
import {
  DISCOVER,
  SELLAR_CREDIT_DIRECT_DEBIT,
  SELLAR_CREDIT_GET_STARTED,
} from "../../../../constants/routes";
import { useBusinessList } from "../../../../hooks/api/v2/business/useBusinessList";
import { InfiniteList } from "../../../../components/infiniteLoading/InfiniteList";
import { SellarStepper } from "../../../../components/stepper/stepper";
import { useLegalEntityUpdate } from "../../../../hooks/api/v2/legalEntity/useLegalEntityUpdate";
import { Switch } from "@sellar-io/components";
import { useBusinessUserList } from "../../../../hooks/api/v2/businessUser/useBusinessUserList";
import { Icon, ProfilePicture, SpinnerLegacy } from "../../../../components";
import { useBusinessUserUpdate } from "../../../../hooks/api/v2/businessUser/useBusinessUserUpdate";
import { useLegalEntity } from "../../../../hooks/api/v2/legalEntity/useLegalEntity";
import { useLegalEntityCreate } from "../../../../hooks/api/v2/legalEntity/useLegalEntityCreate";
import { pluralize } from "@sellar-io/common";
import { useIntercom } from "react-use-intercom";
import { twMerge } from "tailwind-merge";

import { Alert } from "../../../../components/alert/Alert";
import {
  TableLegacy,
  TableLegacyContainer,
  TableLegacyTHead,
  TableLegacyTR,
  TableLegacyTH,
  TableLegacyTBody,
  TableLegacyTD,
} from "../../../../components/table/TableLegacy";
import { useLegalEntityId } from "../../../../hooks/api/v2/legalEntity/useLegalEntityId";
import { useTwoPreverificationAccount } from "../../../../hooks/api/v2/two/useTwoPreverificationAccount";
import { useLegalEntityCompanyCreditInfo } from "../../../../hooks/api/v2/legalEntity/useLegalEntityCompanyCreditInfo";
import { useCreateTwoCustomerUserVerificationLink } from "../../../../hooks/api/v2/two/useCreateTwoCustomerUserVerficationLink";
import { SellarCreditCheckDialogPage } from "./TwoSellarCreditGetStarted.shared";
import {
  SELLAR_CREDIT_ONBOARDING_SOURCE_KEY,
  SELLAR_CREDIT_ONBOARDING_SOURCE_VALUE,
} from "~/constants/sellarConstants.shared";

export const TwoSellarCreditGetStartedContent = () => {
  const { user } = useSellarUser();
  const { business: currentBusiness } = useSellarCurrentBusiness();

  const {
    data: twoPreverificatonAccountData,
    error: twoPreverificatonAccountError,
    isLoading: twoPreverificatonAccountLoading,
  } = useTwoPreverificationAccount();

  const { data: companyCreditInfo, isLoading: companyCreditInfoLoading } =
    useLegalEntityCompanyCreditInfo(
      {
        RetailerId: currentBusiness?.Retailer?.id ?? 0,
      },
      { enabled: !!currentBusiness?.Retailer?.id },
    );

  const legalEntityId = useLegalEntityId();
  const { status: getLegalEntityStatus, data: legalEntity } =
    useLegalEntity(legalEntityId);
  const legalEntityCreateMutation = useLegalEntityCreate();
  const [attemptedCreateLegalEntity, setAttemptedCreateLegalEntity] =
    useState(false);
  const createLegalEntityStarted = useRef(false);
  const [currentStep, setCurrentStep] = useState<SellarCreditCheckDialogPage>(
    SellarCreditCheckDialogPage.COMPLETED_TWO_ONBOARDING,
  );

  const { data: businessList, status: getBusinessListQueryStatus } =
    useBusinessList({
      query: [
        ...(user?.type !== "admin"
          ? [
              Object.freeze({
                type: "hasMembership",
                modifier: "EQUALS",
                value: true,
              }),
            ]
          : []),
        {
          type: "type",
          modifier: "EQUALS",
          value: "retailer",
        } as const,
      ],

      sort: [
        {
          direction: "asc",
          type: "type",
        },
        {
          direction: "asc",
          type: "name",
        },
      ],
      limit: 10,
    });

  const userAttachedToMultipleBusinesses = useMemo(() => {
    const businessListLength = businessList?.pages[0]?.length ?? 0;
    return businessListLength > 1;
  }, [businessList]);

  // attempt legal entity creation as merchant customer user is verified

  useEffect(() => {
    if (
      !createLegalEntityStarted.current &&
      getLegalEntityStatus === "error" &&
      currentBusiness?.id &&
      twoPreverificatonAccountData?.isMerchantCustomerUserVerified
    ) {
      createLegalEntityStarted.current = true;
      legalEntityCreateMutation
        .mutateAsync({ businessId: currentBusiness.id })
        .finally(() => setAttemptedCreateLegalEntity(true));
    }
  }, [
    currentBusiness?.id,
    getLegalEntityStatus,
    twoPreverificatonAccountData?.isMerchantCustomerUserVerified,
    legalEntityCreateMutation,
  ]);

  if (
    getLegalEntityStatus === "pending" ||
    legalEntityCreateMutation.isPending ||
    companyCreditInfoLoading ||
    twoPreverificatonAccountLoading ||
    getBusinessListQueryStatus === "pending"
  ) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <SpinnerLegacy />
      </div>
    );
  }

  // if supplier or already approved (indicates legal entity exists), redirect

  if (currentBusiness?.Supplier) {
    return <Navigate to={DISCOVER} />;
  }

  // two account/company found, but no credit offered

  if (
    !!companyCreditInfo &&
    ["declined", "noCreditOffered"].includes(companyCreditInfo.status)
  ) {
    return <NotEligibleForCredit />;
  }

  // no two preverification found, indicates onboarding hasn't been started

  if (twoPreverificatonAccountError && !legalEntity) {
    return <NoTwoAccountFound />;
  }

  // unverified two preverification found, indicates onboarding hasn't been started

  if (
    twoPreverificatonAccountData &&
    !twoPreverificatonAccountData.isMerchantCustomerUserVerified &&
    !legalEntity
  ) {
    return <TwoVerificationIncomplete />;
  }

  // error occurred during legal entity creation

  if (
    attemptedCreateLegalEntity &&
    legalEntityCreateMutation.status === "error" &&
    getLegalEntityStatus === "error"
  ) {
    return <LegalEntityCreationError />;
  }

  if (currentStep === SellarCreditCheckDialogPage.COMPLETED_TWO_ONBOARDING) {
    return (
      <CompletedTwoOnboarding
        onClickContinue={() => {
          // user attached to one business, skip attach businesses to legal entity step
          if (userAttachedToMultipleBusinesses) {
            return setCurrentStep(
              SellarCreditCheckDialogPage.ATTACH_BUSINESSES_TO_LEGAL_ENTITY,
            );
          }
          setCurrentStep(
            SellarCreditCheckDialogPage.ASSIGN_SELLAR_CREDIT_ACCESS,
          );
        }}
      />
    );
  }

  if (
    currentStep ===
      SellarCreditCheckDialogPage.ATTACH_BUSINESSES_TO_LEGAL_ENTITY &&
    currentBusiness
  ) {
    return (
      <AttachBusinessesToLegalEntity
        legalEntityName={legalEntity?.name ?? ""}
        currentBusinessId={currentBusiness.id}
        businessesAttachedToLegalEntity={
          legalEntity?.businessList.map((business) => business.id) ?? []
        }
        onClickContinue={() => {
          setCurrentStep(
            SellarCreditCheckDialogPage.ASSIGN_SELLAR_CREDIT_ACCESS,
          );
        }}
        onClickSkip={() => {
          setCurrentStep(
            SellarCreditCheckDialogPage.ASSIGN_SELLAR_CREDIT_ACCESS,
          );
        }}
      />
    );
  }

  if (currentStep === SellarCreditCheckDialogPage.ASSIGN_SELLAR_CREDIT_ACCESS) {
    return (
      <AssignSellarCreditAccess
        businessesAttachedToLegalEntity={legalEntity?.businessList ?? []}
        showProgressStepper={userAttachedToMultipleBusinesses} // skipped AttachBusinessesToLegalEntity step, don't show progress
        onClickComplete={() => {
          const directDebitUrl = new URL(
            SELLAR_CREDIT_DIRECT_DEBIT,
            window.location.origin,
          );

          directDebitUrl.searchParams.set(
            SELLAR_CREDIT_ONBOARDING_SOURCE_KEY,
            SELLAR_CREDIT_ONBOARDING_SOURCE_VALUE,
          );

          // hard redirect required to ensure the user is refetched
          window.location.replace(directDebitUrl.toString());
        }}
      />
    );
  }
};

// pages

const NoTwoAccountFound = () => {
  const intercom = useIntercom();
  const navigate = useNavigate();

  return (
    <div className="flex h-screen items-center justify-center px-2 text-center">
      <div className="max-w-[636px]">
        <div className="flex flex-col items-center justify-center gap-4">
          <img
            src="https://sellar.imgix.net/sellar-image-assets/sellar-credit/no-account-found.svg"
            className="w-[64px]"
          />

          <div className="text-2xl font-semibold text-zinc-800">
            Looks like you haven't started Sellar Credit onboarding
          </div>
          <div className="text-zinc-800">
            To use Sellar Credit you need to complete onboarding with our
            financial partners Two inc. Click the button below to continue
            onboarding with them, or Contact support if you need help with your
            onboarding
          </div>

          <div className="flex items-center gap-2 py-4">
            <Button
              color={"secondary"}
              className={"w-full md:w-[320px]"}
              onClick={() => intercom.show()}
            >
              Contact support
            </Button>

            <Button
              color={"primary"}
              className={"w-full md:w-[320px]"}
              onClick={() => {
                navigate(`${DISCOVER}?showSellarCreditOnboarding=true`);
              }}
            >
              Get started
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const TwoVerificationIncomplete = () => {
  const intercom = useIntercom();

  const createTwoCustomerUserVerificationLink =
    useCreateTwoCustomerUserVerificationLink();

  return (
    <div className="flex h-screen items-center justify-center px-2 text-center">
      <div className="max-w-[636px]">
        <div className="flex flex-col items-center justify-center gap-4">
          <img
            src="https://sellar.imgix.net/sellar-image-assets/sellar-credit/alert-bell-icon.svg"
            className="w-[64px]"
          />

          <div className="text-2xl font-semibold text-zinc-800">
            You need to complete Two inc account verification to start using
            Sellar Credit
          </div>
          <div className="text-zinc-800">
            Follow the link below to complete verification with Two inc
          </div>

          <div className="flex items-center gap-2 py-4">
            <Button
              color={"secondary"}
              className={"w-full md:w-[320px]"}
              onClick={() => intercom.show()}
            >
              Contact support
            </Button>

            <Button
              color={"primary"}
              className={"w-full md:w-[320px]"}
              disabled={createTwoCustomerUserVerificationLink.isPending}
              onClick={() => {
                // create the verification link and redirect user
                const returnUrl = `${import.meta.env.VITE_URL}${SELLAR_CREDIT_GET_STARTED}`;
                createTwoCustomerUserVerificationLink
                  .mutateAsync({ returnUrl })
                  .then((result) => {
                    window.open(result.redirectUrl, "_blank");
                  });
              }}
            >
              Verify account
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const LegalEntityCreationError = () => {
  const intercom = useIntercom();
  const navigate = useNavigate();

  return (
    <div className="flex h-screen items-center justify-center px-2 text-center">
      <div className="max-w-[636px]">
        <div className="flex flex-col items-center justify-center gap-4">
          <img
            src="https://sellar.imgix.net/sellar-image-assets/sellar-credit/no-account-found.svg"
            className="w-[64px]"
          />

          <div className="text-2xl font-semibold text-zinc-800">
            We’ve run into a problem setting up your Company on Sellar
          </div>
          <div className="text-zinc-800">
            This may be due to your company number being already in use in our
            system, or more than one user registering on Two inc with your
            company number. Please contact support for more information
          </div>

          <div className="flex items-center gap-2 py-4">
            <Button
              color={"secondary"}
              className={"w-full md:w-[320px]"}
              onClick={() => navigate(DISCOVER)}
            >
              Back to market
            </Button>

            <Button
              color={"primary"}
              className={"w-full md:w-[320px]"}
              onClick={() => intercom.show()}
            >
              Contact support
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const NotEligibleForCredit = () => {
  const navigate = useNavigate();
  return (
    <div className="flex h-screen items-center justify-center px-2 text-center">
      <div className="max-w-[636px]">
        <div className="flex flex-col items-center justify-center gap-4">
          <div className="text-2xl font-semibold text-zinc-800">
            We’re sorry, our financial partner Two inc is unable to offer you
            credit
          </div>
          <div className="text-zinc-800">
            Unfortunately Two inc cannot offer you trade credit at this time.
            But you can still set up your usual credit terms with suppliers
            directly on Sellar
          </div>

          <Alert
            type={"success"}
            title={"Get credit terms with your suppliers"}
            message={
              "Add them to your list to send a message to request your usual terms and pricing. "
            }
            className={"w-full"}
          />

          <div className="flex items-center gap-2 py-4">
            <Button
              color={"secondary"}
              className={"w-full md:w-[320px]"}
              onClick={() =>
                window.open(
                  "https://intercom.help/sellario/en/articles/10722487-buyer-guide-i-ve-been-declined-for-sellar-credit",
                  "_blank",
                )
              }
            >
              Learn more
            </Button>

            <Button
              color={"primary"}
              className={"w-full md:w-[320px]"}
              onClick={() => navigate(-1)}
            >
              I understand
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const CompletedTwoOnboarding = ({
  onClickContinue,
}: {
  onClickContinue: () => void;
}) => {
  return (
    <div className="flex h-screen flex-col justify-center px-4">
      <div className="flex flex-col items-center justify-center">
        <img
          src="https://sellar.imgix.net/sellar-image-assets/app-assets/sellar-approve-badge.png"
          className="w-[77px]"
          width={77}
          height={77}
        />
        <div className="mb-4 text-2xl font-semibold text-zinc-800">
          Thanks for completing your onboarding with Two inc!
        </div>
        <div className="mb-12 text-zinc-800">
          You’re just a couple of steps away from completing your Sellar Credit
          set up
        </div>

        <Button
          color={"primary"}
          className={"w-full md:w-[320px]"}
          onClick={() => onClickContinue()}
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

const AttachBusinessesToLegalEntity = ({
  onClickContinue,
  onClickSkip,
  legalEntityName,
  businessesAttachedToLegalEntity,
  currentBusinessId,
}: {
  onClickContinue: () => void;
  onClickSkip: () => void;
  legalEntityName: string;
  businessesAttachedToLegalEntity: number[];
  currentBusinessId: number;
}) => {
  const intercom = useIntercom();
  const [ownershipConfirmation, setOwnershipConfirmation] = useState(false);
  const legalEntityUpdateMutation = useLegalEntityUpdate();
  const [selectedBusinessList, setSelectedBusinessesList] = useState(() => [
    ...new Set([...businessesAttachedToLegalEntity, currentBusinessId]),
  ]);
  const selectedBusinessCount = selectedBusinessList.length;

  const onUpdateBusinessesAttachedToLegalEntity = () => {
    legalEntityUpdateMutation
      .mutateAsync({ businessIdList: selectedBusinessList })
      .then(() => {
        onClickContinue();
      })
      .catch((_error) => {
        console.error("Error updating legal entity attached businesses");
      });
  };

  return (
    <div className="my-9 flex justify-center px-4 text-center">
      <div className="w-full max-w-[800px]">
        <img
          src="https://sellar.imgix.net/sellar-image-assets/sellar-credit/sellar-credit-account-set-up.png"
          className="mx-auto mb-9 w-[236px]"
          width={236}
          height={24}
        />

        <div className="mb-9 flex justify-center">
          <SellarStepper
            steps={2}
            activeStep={1}
            className="w-[60%] max-w-[300px]"
          />
        </div>

        <div className="mb-4 text-2xl font-semibold text-zinc-800">
          Which businesses would you like to share your credit with?
        </div>

        <div className="mb-8 text-zinc-800">
          You can share your Sellar Credit balance across any businesses owned
          by {legalEntityName}
        </div>

        <div className="mb-2 max-h-[40vh] min-h-[40vh] overflow-auto">
          <BusinessCardList
            selectedBusinessList={selectedBusinessList}
            onToggleBusiness={(businessId) => {
              setSelectedBusinessesList((currentList) => {
                if (currentList.includes(businessId)) {
                  return currentList.filter((id) => id !== businessId);
                }

                return [...currentList, businessId];
              });
            }}
          />
        </div>

        <div className="mb-6 text-left text-sm text-zinc-500">
          Can't see all of your businesses?{" "}
          <span
            className="cursor-pointer underline"
            onClick={() => intercom.show()}
          >
            Contact support
          </span>
        </div>

        <SellarCreditStatusBar
          className={"my-4 rounded-lg border border-emerald-300 bg-emerald-100"}
        >
          <div className="text-emerald-800">
            <span className="font-semibold">
              {selectedBusinessCount}{" "}
              {pluralize("business", selectedBusinessCount)}
            </span>{" "}
            with access to Sellar Credit
          </div>
        </SellarCreditStatusBar>

        <div className="mb-4">
          <BusinessClaimConfirmation
            isConfirmed={ownershipConfirmation}
            onChange={(confirm) => {
              setOwnershipConfirmation(confirm);
            }}
            legalEntityName={legalEntityName}
          />
        </div>

        <div className="flex gap-2">
          <Button
            color={"secondary"}
            className={"w-1/2"}
            onClick={() => onClickSkip()}
          >
            Skip for now
          </Button>
          <Button
            color={"primary"}
            className={"w-1/2 text-wrap"}
            onClick={() => onUpdateBusinessesAttachedToLegalEntity()}
            disabled={!ownershipConfirmation}
          >
            Add selected {pluralize("business", selectedBusinessList.length)}
          </Button>
        </div>
      </div>
    </div>
  );
};

const AssignSellarCreditAccess = ({
  onClickComplete,
  businessesAttachedToLegalEntity,
  showProgressStepper = true,
}: {
  onClickComplete: () => void;
  businessesAttachedToLegalEntity: {
    id: number;
    name: string;
    businessUsersWithCreditAccess: { id: number }[];
  }[];
  showProgressStepper: boolean;
}) => {
  const { business: currentBusiness } = useSellarCurrentBusiness();

  const description =
    businessesAttachedToLegalEntity.length === 1
      ? `Only you have control over which users at ${businessesAttachedToLegalEntity[0]?.name} have access to your Sellar Credit balance.`
      : `Only you have control over which users at your businesses have access
          to your Sellar Credit balance`;

  return (
    <div className="mt-9 flex justify-center px-4 text-center">
      <div className="w-full max-w-[800px]">
        <img
          src="https://sellar.imgix.net/sellar-image-assets/sellar-credit/sellar-credit-account-set-up.png"
          className="mx-auto mb-9 w-[236px]"
          width={236}
          height={24}
        />

        <div className="mb-9 flex justify-center">
          {showProgressStepper && (
            <SellarStepper
              steps={2}
              activeStep={2}
              className="w-[60%] max-w-[300px]"
            />
          )}
        </div>

        <div className="mb-4 text-2xl font-semibold text-zinc-800">
          Which users would you like to share your credit with?
        </div>

        <div className="mb-8 text-zinc-800">{description}</div>

        <div className="mb-2 flex max-h-[40vh] min-h-[40vh] justify-center overflow-y-auto">
          <div className="flex w-full max-w-[800px] flex-col gap-2">
            {/* show current business first in list */}

            <BusinessAndUsersCreditAccessCard
              businessId={currentBusiness?.id ?? 0}
              businessName={currentBusiness?.name ?? ""}
              isCurrentBusiness={true}
            />

            {/* show other businesses attached to legal entity */}

            {businessesAttachedToLegalEntity
              .filter(({ id }) => id !== currentBusiness?.id)
              .map((business) => {
                return (
                  <BusinessAndUsersCreditAccessCard
                    businessId={business.id}
                    businessName={business.name}
                    isCurrentBusiness={false}
                    key={business.id}
                  />
                );
              })}
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button
            color={"primary"}
            className={"w-1/2"}
            onClick={() => onClickComplete()}
          >
            Complete
          </Button>
        </div>
      </div>
    </div>
  );
};

// components

const BusinessCardList = ({
  selectedBusinessList,
  onToggleBusiness,
}: {
  selectedBusinessList: number[];
  onToggleBusiness: (businessId: number) => void;
}) => {
  const { business: currentBusiness } = useSellarCurrentBusiness();
  const { user } = useSellarUser();
  const businessesQuery = useBusinessList({
    query: [
      ...(user?.type !== "admin"
        ? [
            Object.freeze({
              type: "hasMembership",
              modifier: "EQUALS",
              value: true,
            }),
          ]
        : []),
      {
        type: "type",
        modifier: "EQUALS",
        value: "retailer",
      } as const,
    ],

    sort: [
      {
        direction: "asc",
        type: "type",
      },
      {
        direction: "asc",
        type: "name",
      },
    ],
    limit: 50,
  });

  return (
    <div className="overflow-hidden rounded-xl border border-zinc-300">
      {/* current business at top of list */}

      <BusinessCard
        isBusinessSelected={true}
        businessId={currentBusiness?.id ?? 0}
        businessName={currentBusiness?.name ?? ""}
        onToggleBusiness={() => {}}
        isCurrentBusiness={true}
      />

      {/* all other businesses the user is attached to */}

      <InfiniteList query={businessesQuery}>
        {businessesQuery.data &&
          businessesQuery.data.pages.map((page) =>
            page
              .filter(({ id }) => id !== currentBusiness?.id)
              .map((business) => {
                const isBusinessSelected = selectedBusinessList.includes(
                  business.id,
                );
                return (
                  <BusinessCard
                    key={business.id}
                    isCurrentBusiness={false}
                    businessId={business.id}
                    businessName={business.name}
                    onToggleBusiness={onToggleBusiness}
                    isBusinessSelected={isBusinessSelected}
                  />
                );
              }),
          )}
      </InfiniteList>
    </div>
  );
};

const BusinessCard = ({
  businessId,
  businessName,
  onToggleBusiness,
  isBusinessSelected,
  isCurrentBusiness,
}: {
  businessId: number;
  businessName: string;
  onToggleBusiness: (businessId: number) => void;
  isBusinessSelected: boolean;
  isCurrentBusiness: boolean;
}) => {
  return (
    <div
      key={businessId}
      className={twMerge(
        "first flex cursor-pointer justify-between border-b border-zinc-300 bg-white p-4",
        isCurrentBusiness && "cursor-default",
        isBusinessSelected && "bg-zinc-50",
      )}
      onClick={() => {
        if (!isCurrentBusiness) {
          onToggleBusiness(businessId);
        }
      }}
    >
      <div className="items-center text-sm font-semibold text-zinc-700">
        {businessName}{" "}
        {isCurrentBusiness && (
          <span className="font-normal">(currently logged in)</span>
        )}
      </div>
      <input
        disabled={isCurrentBusiness}
        checked={isBusinessSelected}
        onChange={() => {}}
        type={"checkbox"}
      />
    </div>
  );
};

const BusinessClaimConfirmation = ({
  isConfirmed,
  onChange,
  legalEntityName,
}: {
  isConfirmed: boolean;
  onChange: (value: boolean) => void;
  legalEntityName: string;
}) => {
  return (
    <label className="flex cursor-pointer gap-4 rounded-lg border border-indigo-600 bg-indigo-50 p-4 text-left">
      <InputCheckbox
        variant="circular"
        onChange={(event) => onChange(event.target.checked)}
        containerClassName="flex-none"
        checked={isConfirmed}
      />
      <div>
        <div className="font-semibold text-zinc-800">
          Ownership confirmation
        </div>
        <p className="text-zinc-800">
          I confirm that the businesses selected operate under the{" "}
          {legalEntityName} registered company name and take full responsibility
          for their usage of Sellar Credit
        </p>
      </div>
    </label>
  );
};

const SellarCreditStatusBar = ({
  className,
  children,
}: {
  className?: string;
  children: ReactElement;
}) => {
  return <div className={twMerge("py-2", className)}>{children}</div>;
};

const BusinessAndUsersCreditAccessCard = ({
  businessId,
  businessName,
  isCurrentBusiness,
}: {
  businessId: number;
  businessName: string;
  isCurrentBusiness: boolean;
}) => {
  const { user } = useSellarUser();
  const [showBusinessUserList, setShowBusinessUserList] =
    useState(isCurrentBusiness);

  return (
    <div className="rounded-lg bg-white p-4">
      <BusinessTitle
        className={twMerge(showBusinessUserList && "pb-4")}
        businessName={businessName}
        isCurrentBusiness={isCurrentBusiness}
        isExpanded={showBusinessUserList}
        onToggleExpand={() => {
          setShowBusinessUserList((current) => !current);
        }}
      />
      {showBusinessUserList && (
        <BusinessUserList
          businessId={businessId}
          currentUserId={user?.id ?? 0}
        />
      )}
    </div>
  );
};

const BusinessTitle = ({
  className,
  businessName,
  isCurrentBusiness,
  isExpanded,
  onToggleExpand,
}: {
  className?: string;
  businessName: string;
  isCurrentBusiness: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
}) => {
  return (
    <div className={twMerge("flex items-center justify-between", className)}>
      <div className="text-lg font-semibold text-zinc-800">
        {businessName}{" "}
        {isCurrentBusiness ? (
          <span className="text-base font-normal">(currently logged in)</span>
        ) : (
          ""
        )}
      </div>

      <div>
        <Button
          className={
            "justify-end text-base font-normal text-zinc-500 normal-case"
          }
          size={"small"}
          color={"transparent"}
          onClick={() => onToggleExpand()}
        >
          {isExpanded ? "Hide users" : "Show users"}
          <Icon icon={isExpanded ? "CHEVRON_UP" : "CHEVRON_DOWN"} />
        </Button>
      </div>
    </div>
  );
};

const BusinessUserList = ({
  businessId,
  currentUserId,
}: {
  businessId: number;
  currentUserId: number;
}) => {
  const businessUserListQuery = useBusinessUserList({
    query: [{ type: "BusinessId", modifier: "EQUALS", value: businessId }],
    sortBy: "name",
    sortDir: "asc",
  });

  const currentBusinessUser = businessUserListQuery.data?.pages[0]?.find(
    (bu) => bu.User.id === currentUserId,
  );
  const allOtherUsers =
    businessUserListQuery.data?.pages[0]?.filter(
      (bu) => bu.User.id !== currentUserId,
    ) ?? [];

  return (
    <TableLegacyContainer>
      <TableLegacy>
        <TableLegacyTHead>
          <TableLegacyTR>
            <TableLegacyTH className={"sticky top-0"}>User name</TableLegacyTH>
            <TableLegacyTH className={"sticky top-0"}>
              Sellar Credit access
            </TableLegacyTH>
            <TableLegacyTH className={"sticky top-0"}>
              Balance visible
            </TableLegacyTH>
          </TableLegacyTR>
        </TableLegacyTHead>
        <TableLegacyTBody>
          {[
            ...(currentBusinessUser ? [currentBusinessUser] : []),
            ...allOtherUsers,
          ].map((businessUser) => {
            const { name, email, profileImageUrl } = businessUser.User;
            const isMe = businessUser.User.id === currentUserId;

            return (
              <BusinessUser
                key={businessUser.id}
                businessUser={{
                  id: businessUser.id,
                  name,
                  email,
                  profileImageUrl,
                }}
                isMe={isMe}
                canAccessSellarCredit={businessUser.canAccessSellarCredit}
                canViewSellarCreditBalance={
                  businessUser.canViewSellarCreditBalance
                }
              />
            );
          })}
        </TableLegacyTBody>
      </TableLegacy>
    </TableLegacyContainer>
  );
};

const BusinessUser = ({
  businessUser,
  isMe,
  canAccessSellarCredit,
  canViewSellarCreditBalance,
}: {
  businessUser: {
    id: number;
    name: string | null;
    email: string;
    profileImageUrl: string | null;
  };
  isMe: boolean;
  canAccessSellarCredit: boolean;
  canViewSellarCreditBalance: boolean;
}) => {
  const businessUserUpdateMutation = useBusinessUserUpdate(businessUser.id);

  return (
    <>
      <TableLegacyTR>
        <TableLegacyTD>
          <div className={"flex items-center justify-start gap-3"}>
            <ProfilePicture
              name={businessUser.name}
              logoUrl={businessUser.profileImageUrl}
            />
            <div className="flex flex-col text-left">
              <H5 className="break-words">
                {businessUser.name} {isMe && "(me)"}
              </H5>
              <H6 className="break-all select-text">
                {[businessUser.email].filter(Boolean).join(" - ")}
              </H6>
            </div>
          </div>
        </TableLegacyTD>
        <TableLegacyTD>
          <Switch
            disabled={isMe || businessUserUpdateMutation.isPending}
            checked={canAccessSellarCredit}
            onChange={() => {
              businessUserUpdateMutation.mutateAsync({
                canAccessSellarCredit: !canAccessSellarCredit,
              });
            }}
          />
        </TableLegacyTD>
        <TableLegacyTD>
          <Switch
            disabled={isMe || businessUserUpdateMutation.isPending}
            checked={canViewSellarCreditBalance}
            onChange={() => {
              businessUserUpdateMutation.mutateAsync({
                canViewSellarCreditBalance: !canViewSellarCreditBalance,
              });
            }}
          />
        </TableLegacyTD>
      </TableLegacyTR>
    </>
  );
};
